/**
 * Slug field with live format feedback and a duplicate check.
 *
 * Slugs end up in public URLs, so a bad one either 404s or — for a duplicate —
 * makes the storefront resolve the wrong record. The API rejects both (see
 * crud-proxy.ts), but a save-time toast is a poor place to learn it, so this
 * surfaces the problem while typing and offers a one-click fix.
 *
 * Deliberately NOT auto-correcting on every keystroke: the user may be mid-word
 * ("red-" on the way to "red-wine"), and silently rewriting input is hostile.
 * Normalisation happens on blur, where the value is settled.
 */
import { useEffect, useRef, useState } from "react";
import { AlertCircle, Check, Loader2, Wand2 } from "lucide-react";
import { supabase, slugify, slugProblem } from "@vinoga/db";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type Props = {
  value: string;
  onChange: (next: string) => void;
  /** Table to check uniqueness against (e.g. "products"). */
  table: string;
  /** Row being edited — excluded from the duplicate check so it doesn't
   *  collide with itself. Omit when creating. */
  excludeId?: string;
  /** Text to derive a slug from when the field is empty (usually the LV name). */
  sourceText?: string;
  /** Locale for a per-locale slug; omit for the canonical one. */
  locale?: string;
  className?: string;
  placeholder?: string;
};

type Status =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "ok" }
  | { kind: "invalid"; message: string }
  | { kind: "taken" };

export function SlugInput({
  value,
  onChange,
  table,
  excludeId,
  sourceText,
  locale,
  className,
  placeholder,
}: Props) {
  const t = useT();
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  // Guards against a slow earlier request overwriting a newer verdict.
  const reqRef = useRef(0);

  const suggestion = slugify(value || sourceText || "");
  const canFix = suggestion.length > 0 && suggestion !== value;

  useEffect(() => {
    const raw = value.trim();
    if (!raw) {
      setStatus({ kind: "idle" });
      return;
    }

    const problem = slugProblem(raw);
    if (problem) {
      setStatus({
        kind: "invalid",
        message:
          problem === "too_long"
            ? t("slug.tooLong", "Saite ir pārāk gara (maksimums 200 simboli).")
            : t(
                "slug.invalidChars",
                "Atļauti tikai mazie burti bez garumzīmēm, cipari un defises.",
              ),
      });
      return;
    }

    // Debounced uniqueness probe — one request after typing settles.
    setStatus({ kind: "checking" });
    const seq = ++reqRef.current;
    const timer = setTimeout(async () => {
      let q = supabase.from(table).select("id").eq("slug", raw).limit(1);
      if (excludeId) q = q.neq("id", excludeId);
      const { data, error } = await q;
      if (seq !== reqRef.current) return; // a newer keystroke superseded this
      if (error) {
        // Don't claim the slug is fine if we couldn't check — stay quiet and
        // let the API have the final say on save.
        setStatus({ kind: "idle" });
        return;
      }
      setStatus((data ?? []).length > 0 ? { kind: "taken" } : { kind: "ok" });
    }, 400);

    return () => clearTimeout(timer);
  }, [value, table, excludeId, t]);

  const bad = status.kind === "invalid" || status.kind === "taken";

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          // Normalise once the value has settled, so partial typing is left alone.
          onBlur={() => {
            const cleaned = slugify(value);
            if (cleaned && cleaned !== value) onChange(cleaned);
          }}
          className={cn(
            className,
            "pr-9",
            bad && "border-destructive focus:border-destructive",
          )}
          placeholder={placeholder ?? "chateau-margaux-2018"}
          spellCheck={false}
          autoCapitalize="none"
          autoCorrect="off"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
          {status.kind === "checking" && (
            <Loader2 size={14} className="animate-spin text-muted-foreground" />
          )}
          {status.kind === "ok" && <Check size={14} className="text-emerald-600" />}
          {bad && <AlertCircle size={14} className="text-destructive" />}
        </span>
      </div>

      {status.kind === "invalid" && (
        <p className="font-body text-xs text-destructive">{status.message}</p>
      )}
      {status.kind === "taken" && (
        <p className="font-body text-xs text-destructive">
          {t("slug.taken", "Šī saite jau tiek izmantota citam ierakstam.")}
        </p>
      )}

      {canFix && (
        <button
          type="button"
          onClick={() => onChange(suggestion)}
          className="inline-flex items-center gap-1.5 font-body text-xs text-primary hover:underline"
        >
          <Wand2 size={12} />
          {t("slug.useSuggestion", "Izmantot")}: <span className="font-mono">{suggestion}</span>
        </button>
      )}

      {locale && (
        <p className="font-body text-xs text-muted-foreground">
          {t("slug.localeHint", "Neobligāti — ja tukšs, tiks izmantota pamata saite.")}
        </p>
      )}
    </div>
  );
}
